import i18n from '@/libs/i18n';
import router from '@/router';

/**
 * Vuex actions for managing vendor products.
 * @module store/vendor/products/actions
 */
export default {
  /**
   * Fetch vendor products based on filters.
   * @async
   * @function get
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing filters for fetching vendor products.
   */
  async get({ commit }, data) {
    commit('loadingStart', null, { root: true });

    const filters = window.mapFilterData(data);

    await this._vm.$http
      .get(`users/vendors/${data.vendor_id}/products${filters}`)
      .then(respo => {
        if (respo.status === 200) {

          commit('unsetProducts');
          setTimeout(() => {
            commit('setData', respo.data.data);
          }, 0);


          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Export vendor products to Excel.
   * @async
   * @function exportExcel
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing filters for exporting vendor products.
   */
  async exportExcel({ commit }, data) {
    commit('loadingStart', null, { root: true });


    await this._vm.$http
      .get(`users/vendors/${data.vendor_id}/products/export-excel`, { responseType: 'blob' })
      .then(respo => {
        if (respo.status === 200) {
          const url = window.URL.createObjectURL(new Blob([respo.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'products.xlsx'); //or any other extension
          document.body.appendChild(link);
          link.click();
          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Import vendor products from Excel.
   * @async
   * @function importExcel
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing the Excel file to import.
   * @returns {Promise} Promise representing the HTTP request.
   */
  async importExcel({ commit }, data) {
    commit('loadingStart', null, { root: true });


    return await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/import-excel`, data)
      .then(respo => {
        if (respo.status === 200) {

          commit('loadingFinish', null, { root: true });
          return true;
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Fetch vendor product details.
   * @async
   * @function detail
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing the id of the vendor product to fetch.
   */
  async detail({ commit }, data) {
    commit('loadingStart', null, { root: true });

    await this._vm.$http
      .get(`users/vendors/${data.vendor_id}/products/${data.id}`)
      .then(respo => {
        if (respo.status === 200) {
          commit('setDetail', respo.data.data);

          commit('loadingFinish', null, { root: true });

        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },


  /**
   * Unset vendor product details.
   * @async
   * @function unsetDetail
   * @param {Object} context - Vuex action context.
   */
  async unsetDetail({ commit }) {
    commit('unsetDetail');
  },

  /**
   * Reorder vendor products.
   * @async
   * @function reorder
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing the new order of vendor products.
   */
  async reorder({ commit }, data) {
    commit('loadingStart', null, { root: true });
    await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/reorder/${data.id}`, data)
      .then(respo => {
        if (respo.status === 200) {
          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {

        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Toggle vendor product status.
   * @async
   * @function toggle
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing the id of the vendor product to toggle.
   */
  async toggle({ commit }, data) {
    commit('loadingStart', null, { root: true });


    await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/toggle`, data)
      .then(respo => {
        if (respo.status === 200) {


          commit('updateStatusData', respo.data.data);
          commit('successMessage', 'toggle', { root: true });
          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('failMessage', 'toggle', { root: true });
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Create a new vendor product.
   * @async
   * @function create
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data for the new vendor product.
   */
  async create({ commit }, data) {
    commit('loadingStart', null, { root: true });
    await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/create`, data)
      .then(respo => {
        if (respo.status === 200) {
          router.push({ name: 'users-vendors-products', params: { lang: i18n.locale, id: data.vendor_id } });
          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Update a vendor product.
   * @async
   * @function update
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data for the vendor product to update.
   */
  async update({ commit }, data) {
    commit('loadingStart', null, { root: true });
    await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/update/${data.id}`, data)
      .then(respo => {
        if (respo.status === 200) {
          router.push({ name: 'users-vendors-products', params: { lang: i18n.locale, id: data.vendor_id } });
          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Delete a vendor product.
   * @async
   * @function delete
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data for the vendor product to delete.
   */
  async delete({ commit }, data) {
    commit('loadingStart', null, { root: true });
    await this._vm.$http
      .post(`users/vendors/${data.vendor_id}/products/delete/${data.id}`)
      .then(respo => {
        if (respo.status === 200) {
          commit('successMessage', 'delete', { root: true });
          commit('loadingFinish', null, { root: true });

          commit('deleteItem', data.id);
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  },

  /**
   * Fetch vendor product categories.
   * @async
   * @function getCategories
   * @param {Object} context - Vuex action context.
   * @param {Object} data - Data containing the id of the vendor.
   */
  async getCategories({ commit }, data) {

    await this._vm.$http
      .get(`users/vendors/${data.vendor_id}/products/categories`)
      .then(respo => {
        if (respo.status === 200) {
          commit('setCategories', respo.data.data);

          commit('loadingFinish', null, { root: true });
        }
      })
      .catch(error => {
        commit('loadingFinish', null, { root: true });
        throw error;
      });
  }

};
